import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { APP_VERSION } from "../constants/appVersion";

ChartJS.register(...registerables);

const utahData = "/img/UTDT_Logo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

const EngTable15 = ({ data }) => {
  const pdfRef = useRef();

  // Convert values
  const cleanedData = data.map((item) => ({
    ...item,
    y23: Number(item.year_2023),
    y24: Number(item.year_2024),
    diff: Number(item.year_2024) - Number(item.year_2023),
    pct: (
      ((Number(item.year_2024) - Number(item.year_2023)) /
        Number(item.year_2023)) *
      100
    ).toFixed(2),
  }));

  // Insights
  const maxItem = cleanedData.reduce((prev, curr) =>
    curr.diff > prev.diff ? curr : prev,
  );

  const totalGrowth = cleanedData.reduce((sum, i) => sum + i.diff, 0);
  const avgGrowth = (totalGrowth / cleanedData.length).toFixed(2);

  const chartData = {
    labels: cleanedData.map((item) => item.education_label),
    datasets: [
      {
        label: "2023 Employment",
        data: cleanedData.map((item) => item.y23),
        backgroundColor: "#D8F999",
      },
      {
        label: "2024 Employment",
        data: cleanedData.map((item) => item.y24),
        backgroundColor: "#2C92B8",
      },
      {
        label: "Growth Difference",
        data: cleanedData.map((item) => item.diff),
        backgroundColor: "#999933",
        type: "line",
        borderColor: "#999933",
        borderWidth: 2,
        tension: 0.3,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "Employment Growth by Education Level (2023–2024)",
        font: { size: 18, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.raw.toLocaleString("en-US")}`,
        },
      },
    },
    scales: {
      y: { title: { display: true, text: "Employment Count" } },
      x: { title: { display: true, text: "Education Level" } },
    },
  };

  // PDF Export
 const downloadPDF = () => {
   const input = pdfRef.current;
   html2canvas(input, {
     scale: 2,
     useCORS: true,
     backgroundColor: "#ffffff",
     scrollY: -window.scrollY,
   }).then((canvas) => {
     const imgData = canvas.toDataURL("image/png");
     const pdf = new jsPDF("p", "mm", "a4");
     const pageWidth = pdf.internal.pageSize.getWidth();
     const pageHeight = pdf.internal.pageSize.getHeight();
     const imgWidth = pageWidth - 10;
     const imgHeight = (canvas.height * imgWidth) / canvas.width;

     let heightLeft = imgHeight;
     let position = 0;
     let pageNumber = 1;

     const addHeader = () => {
       const logoWidth = 20; // ideal header width
       const logoHeight = 20; // adjust ratio if needed

       pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
       pdf.setFontSize(26);
       pdf.text("UTahData.org", 95, 22);
       pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
       pdf.line(10, 35, 200, 35);
     };

     const addFooter = (pageNumber) => {
       pdf.setFontSize(9);
       const footerY = pageHeight - 10;
       pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
       pdf.text(
         `Downloaded on: ${new Date().toLocaleDateString()}`,
         10,
         footerY,
       );
       pdf.text(
         `Page ${pageNumber}`,
         pageWidth - 10,
         footerY,
         null,
         null,
         "right",
       );
     };

     // First page
     addHeader();
     pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
     heightLeft -= pageHeight - 32;

     // Add more pages if needed
     while (heightLeft > 0) {
       pdf.addPage();
       pageNumber++;
       addHeader();
       position = heightLeft - imgHeight;
       pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
       heightLeft -= pageHeight - 32;
       addFooter(pageNumber);
     }

     // Final footer
     // ===== FINAL FOOTER SAFE AREA =====

     const footerTopY = pageHeight - 40; // start footer 40mm from bottom
     const footerContentY = pageHeight - 28; // logos row
     const footerBottomTextY = pageHeight - 10; // bottom text row

     // Top Divider Line
     pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

     // UtahData logo
     const utahWidth = 18;
     const utahHeight = 18;
     pdf.addImage(
       utahData,
       "PNG",
       20,
       footerContentY - 8,
       utahWidth,
       utahHeight,
     );

     // Built by
     pdf.setFontSize(10);
     pdf.text("Built by", 45, footerContentY);

     // BODEX logo
     const bodexWidth = 36;
     const bodexHeight = bodexWidth * 0.3;
     pdf.addImage(
       bodex,
       "PNG",
       60,
       footerContentY - 6,
       bodexWidth,
       bodexHeight,
     );

     // Using text
     pdf.text("using", 102, footerContentY);

     // KPI logo
     pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

     // Version
     pdf.text(APP_VERSION, 140, footerContentY);

     // Bottom Info Line
     pdf.setFontSize(8);
     pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
     pdf.text(
       `Downloaded on: ${new Date().toLocaleDateString()}`,
       pageWidth - 10,
       footerBottomTextY,
       null,
       null,
       "right",
     );

     pdf.save("EngTable15.pdf");
   });
 };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold text-center mb-4 text-gray-800">
          Employment Growth by Education Level (2023–2024)
        </h2>

        {/* KPI Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="p-4 rounded-xl bg-purple-100 shadow">
            <p className="font-semibold text-purple-900">📈 Highest Growth</p>
            <p className="text-sm text-gray-800">{maxItem.education_label}</p>
            <p className="font-bold text-purple-800">
              {maxItem.diff.toLocaleString("en-US")} Jobs
            </p>
          </div>

          <div className="p-4 rounded-xl bg-green-100 shadow">
            <p className="font-semibold text-green-900">📊 Total Growth</p>
            <p className="font-bold text-green-800">
              {totalGrowth.toLocaleString("en-US")} Jobs
            </p>
          </div>

          <div className="p-4 rounded-xl bg-blue-100 shadow">
            <p className="font-semibold text-blue-900">
              📌 Avg Growth per Category
            </p>
            <p className="font-bold text-blue-800">
              {avgGrowth.toLocaleString("en-US")} Jobs
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <table className="w-full text-left shadow-md border rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-3">Education Level</th>
                    <th>2023</th>
                    <th>2024</th>
                    <th>Growth</th>
                    <th>% Change</th>
                  </tr>
                </thead>
                <tbody>
                  {cleanedData.map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition">
                      <td className="p-2 font-semibold">
                        {row.education_label}
                      </td>
                      <td>{row.y23.toLocaleString("en-US")}</td>
                      <td>{row.y24.toLocaleString("en-US")}</td>
                      <td className="font-semibold text-green-700">
                        {row.diff.toLocaleString("en-US")}
                      </td>
                      <td>{row.pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[800px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
};

export default EngTable15;

